import React from "react";
import { CalendarDay } from "../../../calendar/utils";
import { getComplineLiturgy } from "../../../liturgy/compline";
import { LiturgyViewer } from "../../../liturgy/LiturgyViewer";
import { useDataDependency } from "../../../utils/RemoteDataProvider";
import {
    getEveningReadingsFromMorningReadings,
    LessonData,
    useSettings,
} from "../../../utils/settings";
import { useTrackPray } from "../../../utils/tracking";

export const Compline: React.FC<{ day: CalendarDay }> = (props) => {
    const { day } = props;

    const { settings, setSetting } = useSettings();

    useTrackPray(day, "compline");

    const loadablePsalms = useDataDependency<string[]>({
        type: "proper psalms",
        day,
        psalmCycle: settings.psalmCycle,
        time: "evening",
    });

    const psalms =
        settings.psalmsAtCompline === "compline-short"
            ? [["4", "134"], ["31:1-16"], ["91"]][day.dayOfTheYear % 3]
            : settings.psalmsAtCompline === "compline"
            ? ["4", "31:1-16", "91", "134"]
            : loadablePsalms.state === "loaded"
            ? loadablePsalms.value
            : [];

    const loadableLessons = useDataDependency<{
        firstLesson: LessonData;
        secondLesson: LessonData;
    }>({
        type: "evensong readings",
        day,
        evensongReadings: getEveningReadingsFromMorningReadings(
            settings.mattinsReadings
        ),
        bibleTranslation: settings.bibleTranslation,
        refsOnly: false,
    });

    return (
        <LiturgyViewer
            items={getComplineLiturgy(
                day,
                settings,
                loadablePsalms,
                loadableLessons
            )}
        />
    );

    /*const psalmElements = useMemo(() => {
        return (
            <>
                {psalms.length > 0 ? (
                    psalms.map((psalm) => {
                        return (
                            <Psalm
                                psalmRef={getPsalmRefFromText(psalm)}
                                key={psalm}
                            />
                        );
                    })
                ) : (
                    <LoadingScreen />
                )}
            </>
        );
    }, [psalms, settings]);

    return (
        <>
            <Heading day={day} hour="compline" />
            <Rubric
                content="All standing, the Officiant shall begin with this prayer,
                **Short_Lesson**, and versicle, the People
                responding."
            />
            <TextBlock
                content="The Lord almighty grant us a quiet night and a perfect end.\n
                **Amen.**\n\n
                Brethren, be sober, be vigilant; because your adversary the
                devil, as a roaring lion, walketh about, seeking whom he may
                devour: whom resist, steadfast in the faith.\n\n
                But thou, O Lord, have mercy upon us.\n
                **Thanks be to God.**\n\n
                Our help is in the name of the Lord;\n
                **Who hath made heaven and earth.**"
            />
            <Spacer />
            <Rubric content="All shall kneel and say the **Confession.**" />
            <TextBlock
                content="**Almighty God and Father, we confess to thee, to one another, and
                to the whole host of heaven, that we have sinned against thee,
                through our own fault, in thought, and word, and deed, and in
                those things which we have left undone. For the sake of thy Son
                our Lord Jesus Christ, have mercy upon us, forgive us our sins,
                and by the power of the Holy Ghost raise us up to serve thee in
                newness of life, to the glory of thy holy Name. Amen.**"
            />
            <Spacer />
            <Rubric
                content="The Officiant and People shall sing this
                **Versicle_and_Response.**"
            />
            <TurnUsThen />
            <Spacer />
            <Rubric
                content="All standing, the Officiant and People shall sing these
                **Preces_and_Responses.**"
            />
            <OpeningResponses omitFirst />
            <Spacer />

            {settings.hymnPosition === "before psalms" ? (
                <>
                    <Rubric
                        content="The **Office Hymn** may be sung."
                        settingsPanelId="office hymn"
                    />
                    <OfficeHymn day={day} hour="compline" />
                    <Spacer />
                </>
            ) : (
                <></>
            )}
            <Rubric
                content={`All sitting, the **${
                    psalms.length === 1 ? "Psalm" : "Psalms"
                }**
                shall be sung.`}
                settingsPanelId="compline psalms"
            />
            <PsalmAntiphon />
            {psalmElements}
            <PsalmAntiphon />
            <Spacer />
            <Rubric
                content="The **Lesson** shall_be_read."
                settingsPanelId="compline lesson"
            />
            {settings.secondReadingToCompline === "yes" ? (
                <>
                    {loadableLessons.state === "loaded" &&
                    Array.isArray(loadableLessons.value.secondLesson) ? (
                        <>
                            {loadableLessons.value.secondLesson.map(
                                (lessonPart: {
                                    title: string;
                                    content: any;
                                }) => {
                                    return (
                                        <Scripture
                                            title={lessonPart.title}
                                            content={lessonPart.content}
                                            key={lessonPart.title}
                                        />
                                    );
                                }
                            )}
                            <TextBlock content="The Word of the Lord.\n**Thanks be to God.**" />
                        </>
                    ) : (
                        <LoadingScreen />
                    )}
                </>
            ) : (
                <TextBlock
                    content="Thou, O Lord, art in the midst of us, and we are called by
                thy name. Leave us not, O Lord our God.\n\n**Thanks be to God.**"
                />
            )}
            <Spacer />
            <Rubric content="The Officiant and People shall sing this **Responsory.**" />
            <Responsory />
            <Spacer />
            {settings.hymnPosition === "before canticle" ? (
                <>
                    <Rubric
                        content="The **Office Hymn** may be sung."
                        settingsPanelId="office hymn"
                    />
                    <OfficeHymn day={day} hour="compline" />
                    <Spacer />
                </>
            ) : (
                <></>
            )}
            <Rubric
                content="All standing, the **Gospel_Canticle** shall be sung."
                settingsPanelId="gospel canticles"
            />
            <CanticleAntiphon />
            <Canticle id="nunc dimittis" tone="3a.4" day={day} />
            <CanticleAntiphon />
            <Spacer />
            <Rubric
                content="The Officiant shall sing the **Salutation**, the_People_responding."
                settingsPanelId="salutation"
            />
            <Salutation />
            <Rubric content="All kneeling, the **Prayers** shall be sung." />
            <LesserLitany />
            <LordsPrayer />
            <Rubric content="One or more of the following **Collects** shall be said or sung." />
            <TextBlock
                content="Visit, we beseech thee, O Lord, this place, and drive far from
                it all snares of the enemy; Let thy holy Angels dwell herein to
                preserve us in peace, and may thy blessing be upon us evermore;
                through Jesus Christ our Lord.\n**Amen.**"
            />
            <ProperCollect day={day} hour="compline" optional />
            {settings.hymnPosition === "anthem" ? (
                <>
                    <Rubric
                        content="The **Office Hymn** may be sung."
                        settingsPanelId="office hymn"
                    />
                    <OfficeHymn day={day} hour="compline" />
                    <Spacer />
                </>
            ) : (
                <></>
            )}
            <Rubric content="All standing, the Officiant shall sing, the_People_responding," />
            <LetUsBlessTheLord />
            <TextBlock
                content="The Almighty and Merciful Lord, Father, Son and Holy Ghost,
                bless us and keep us, this night and evermore. **Amen.**"
            />
            <Spacer big />
            <Footer day={day} hour="compline" />
        </>
    );*/
};
